import React, { useEffect, useMemo, useRef, useState } from 'react';
import JoditEditor from 'jodit-react';

const EditorJ = ({
    placeholder,
    onChange,
    onSave,
    onPrint,
    onPreview,
    onFse,
    content,         // contenuto iniziale che arriva dal padre
    adattaStyle = true,
    nascondiSalva = false,
    abilitaFse = false, // abilita il bottone FSE
}) => {
    const editor = useRef(null);

    // Usiamo *un solo* state che rappresenta il contenuto dell'editor
    const [value, setValue] = useState(content || "");

    // Se vogliamo aggiornare l'editor *solo* quando "content" cambia dall'esterno:
    const isInternalChange = useRef(false);

    useEffect(() => {
        if (!isInternalChange.current) {
            setValue(content || "");
        }
        isInternalChange.current = false;
    }, [content]);

    const handleEditorChange = (newContent) => {
        isInternalChange.current = true;
        onChange && onChange(newContent);
    };

    const [zoomLevel, setZoomLevel] = useState(1);

    // I bottoni di default
    const [bottoniDefault, setBottoniDefault] = useState([
        'save', 'print', 'preview', 'undo', 'redo', '|', 'headerDropdown',
        'font', 'fontsize',
        'bold', 'underline', 'italic', 'align', 'ul', 'ol', 'lineHeight',
        'indent', 'link',
        'table',
        'outdent',
        'source',
        'zoomIn', 'zoomOut',
    ]);

    // abilita fse 
    useEffect(() => {
        if (abilitaFse) {
            // se abilita fse è true, evito di fare lo
            // stampa referto cliccando su salva o fse, li setto 
            // anche come stampati
            setBottoniDefault([
                'save', 'FSE', 'print', 'preview', 'undo', 'redo', '|', 'headerDropdown',
                'font', 'fontsize',
                'bold', 'underline', 'italic', 'align', 'ul', 'ol', 'lineHeight',
                'indent', 'link',
                'table',
                'outdent',
                'source',
                'zoomIn', 'zoomOut',
            ])
        };

    }, []);

    // Se nascondo il bottone "Salva"
    useEffect(() => {
        if (nascondiSalva) {
            setBottoniDefault([
                'preview', 'undo', 'redo', '|', 'headerDropdown',
                'font', 'fontsize',
                'bold', 'underline', 'italic', 'align', 'ul', 'ol', 'lineHeight',
                'indent', 'link',
                'table',
                'outdent',
                'source',
                'zoomIn', 'zoomOut',
            ]);
        }
    }, [nascondiSalva]);

    const config = useMemo(() => ({
        readonly: false,
        placeholder: placeholder || "",
        askBeforePasteHTML: false,
        styleValues: `
            p {
              margin-top: 12px !important;
              margin-bottom: -1rem !important;
            }
        `,
        enter: 'br',            // Enter inserisce <br>
        enterBlock: 'p',       // eventuali wrap di testo sciolto in <p> (fallback)
        toolbar: true,
        toolbarSticky: true,
        toolbarStickyOffset: 80,
        toolbarButtonSize: 'large',
        buttons: bottoniDefault,
        plugins: {
            lineHeight: {
                list: ['0.2', '0.5', '0.7', '1', '1.2', '1.3', '1.5', '1.8', '2', '2.5', '3'],
                default: '1',
            }
        },
        events: {
            afterInit: (editor) => {
                if (editor && editor.editorDocument) {
                    const styleTag = editor.editorDocument.createElement("style");
                    styleTag.innerHTML = `
                        .editorj-jodit{
                            font-family: Arial!important;
                            font-size: 14px !important;
                        }
                    `;
                    editor.editorDocument.head.appendChild(styleTag);
                }
            }
        },
        controls: {
            headerDropdown: {
                name: 'Titolo',
                tooltip: 'Seleziona Intestazione',
                list: {
                    p: 'Paragrafo',
                    h1: 'Titolo H1',
                    h2: 'Titolo H2',
                    h3: 'Titolo H3',
                    h4: 'Titolo H4',
                    h5: 'Titolo H5',
                    h6: 'Titolo H6'
                },
                exec: (editor, current, control) => {
                    const headerTag = control.control.args && control.control.args[0];
                    if (headerTag) {
                        editor.selection.commitStyle({ element: headerTag });
                    }
                }
            },
            save: {
                name: 'Salva',
                icon: 'save',
                tooltip: 'Salva contenuto lasciandolo nelle bozze',
                exec: (editor) => {
                    const editorContent = editor.value;
                    onSave && onSave(editorContent, 1);
                }
            },
            FSE: {
                name: 'FSE',
                icon: '',
                tooltip: 'Prepara il referto per l\'invio al FSE, dopo il salvataggio vai nella sezione FSE',
                exec: (editor) => {
                    const editorContent = editor.value;
                    onFse && onFse(editorContent, 1);
                }
            },

            print: {
                name: 'Stampa',
                icon: 'print',
                tooltip: 'Stampa',
                exec: (editor) => {
                    const editorContent = editor.value;
                    onPrint && onPrint(editorContent, 1);
                }
            },
            zoomIn: {
                name: 'Zoom +',
                icon: 'plus',
                tooltip: 'Ingrandisci contenuto',
                exec: () => {
                    setZoomLevel((prevZoom) => {
                        const newZoom = Math.min(prevZoom + 0.1, 2);
                        document.querySelector('.jodit-workplace').style.transform = `scale(${newZoom})`;
                        document.querySelector('.jodit-workplace').style.transformOrigin = 'top left';
                        return newZoom;
                    });
                }
            },
            zoomOut: {
                name: 'Zoom -',
                icon: '<svg width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M5 11h14v2H5z"/></svg>',
                tooltip: 'Riduci contenuto',
                exec: () => {
                    setZoomLevel((prevZoom) => {
                        const newZoom = Math.max(prevZoom - 0.1, 0.5);
                        document.querySelector('.jodit-workplace').style.transform = `scale(${newZoom})`;
                        document.querySelector('.jodit-workplace').style.transformOrigin = 'top left';
                        return newZoom;
                    });
                }
            }
        },
        height: "100%",
        minHeight: "800px",
    }), [placeholder, bottoniDefault]);

    const defaultContainerStyle = {
        height: '120vh',
        display: 'flex',
        flexDirection: 'column'
    };

    const styleEditor = adattaStyle ? defaultContainerStyle : null;

    return (
        <div className='editorj-jodit' style={styleEditor}>
            <JoditEditor
                ref={editor}
                value={value}
                config={config}
                onChange={handleEditorChange}
                style={{ flexGrow: 1, width: '100%' }}
            />
        </div>
    );
};

export default EditorJ;